'use client'

import { useCallback, useEffect, useReducer, useRef } from 'react'
import { useInView } from 'motion/react'
import { ReviewsSlideshow, ReviewsSlideshowLoading } from '@/components/ReviewsSlideshow/ReviewsSlideshow'
import type { Review, ReviewsByScore } from '@/features/memberReviews/memberReviews.types'
import { logger } from '@/services/logger/logger.client'
import { type SbBaseBlockProps } from '@/services/storyblok/storyblok'
import { storyblokEditable } from '@storyblok/react'

const MAX_REVIEWS_PER_SCORE = 10
const MAX_TOTAL_REVIEWS = 20

type Props = SbBaseBlockProps<Record<string, never>>

type State =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'success'; reviews: Array<Review> }
  | { status: 'error' }

type Action = { type: 'LOAD_REVIEWS' } | { type: 'SUCCESS'; payload: { reviews: Array<Review> } } | { type: 'ERROR' }

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'LOAD_REVIEWS':
      return { status: 'loading' }
    case 'SUCCESS':
      return { status: 'success', reviews: action.payload.reviews }
    case 'ERROR':
      return { status: 'error' }
    default:
      return state
  }
}

export function ReviewsSlideshowBlock({ blok }: Props) {
  const ref = useRef<HTMLDivElement>(null)
  const isInView = useInView(ref, { once: true })
  const [state, dispatch] = useReducer(reducer, { status: 'idle' })

  const fetchReviews = useCallback(async () => {
    try {
      dispatch({ type: 'LOAD_REVIEWS' })

      const response = await fetch('/api/member-reviews')
      if (!response.ok) {
        dispatch({ type: 'ERROR' })
        return
      }

      // No reviews returned from the API, so we show an empty state
      if (response.status === 204) {
        dispatch({ type: 'SUCCESS', payload: { reviews: [] } })
        return
      }

      const reviewsByScore = (await response.json()) as ReviewsByScore
      const reviews = shuffleReviews(
        reviewsByScore[5].reviews.slice(0, MAX_REVIEWS_PER_SCORE),
        reviewsByScore[4].reviews.slice(0, MAX_REVIEWS_PER_SCORE),
      )

      dispatch({ type: 'SUCCESS', payload: { reviews } })
    } catch (error) {
      logger.error('ReviewsSlideshow | failed to fetch reviews', { error })
      dispatch({ type: 'ERROR' })
    }
  }, [])

  useEffect(() => {
    if (isInView && state.status === 'idle') {
      fetchReviews()
    }
  }, [fetchReviews, isInView, state.status])

  if (state.status === 'error' || (state.status === 'success' && state.reviews.length === 0)) {
    return null
  }

  return (
    <div ref={ref} {...storyblokEditable(blok)}>
      {(state.status === 'idle' || state.status === 'loading') && <ReviewsSlideshowLoading />}
      {state.status === 'success' && <ReviewsSlideshow reviews={state.reviews} />}
    </div>
  )
}

// Shuffle the reviews so that we don't always show all the reviews for a given score
function shuffleReviews(fiveStarReviews: Array<Review>, fourStarReviews: Array<Review>): Array<Review> {
  const reviews: Array<Review> = []
  const maxLength = Math.max(fiveStarReviews.length, fourStarReviews.length)

  for (let index = 0; index < maxLength && reviews.length < MAX_TOTAL_REVIEWS; index += 1) {
    const fiveStarReview = fiveStarReviews[index]
    const fourStarReview = fourStarReviews[index]

    if (fiveStarReview && reviews.length < MAX_TOTAL_REVIEWS) {
      reviews.push(fiveStarReview)
    }

    if (fourStarReview && reviews.length < MAX_TOTAL_REVIEWS) {
      reviews.push(fourStarReview)
    }
  }

  return reviews
}
